import gql from "graphql-tag.macro";
import { ONE_SECOND } from "../../constants/constants";
import { getClient } from "Globals/api";

const deleteCommentMutation = gql`
    mutation DeleteComment($payload: CommentIdQuery!) {
        DeleteComment(commentId: $payload) {
            archived
            author
            canArchive
            canEdit
            content
            date
            id
            replies
            ts
        }
    }
`;

export const gqlDeleteComment = async commentId => {
    const client = await getClient("comments");
    const data = await client.mutate({
        mutation: deleteCommentMutation,
        variables: { payload: commentId },
    });
    return data;
};

const createCommentMutation = gql`
    mutation CreateComment($payload: CommentContentQuery!) {
        CreateComment(comment: $payload) {
            archived
            author
            canArchive
            canEdit
            content
            date
            id
            replies
            ts
        }
    }
`;

export const gqlCreateComment = async comment => {
    const client = await getClient("comments");
    const { data } = await client.mutate({
        mutation: createCommentMutation,
        variables: { payload: comment },
    });
    return data;
};

const replyToCommentMutation = gql`
    mutation ReplyToComment($payload: CommentQuery!) {
        ReplyToComment(comment: $payload) {
            archived
            author
            canArchive
            canEdit
            content
            date
            id
            replies
            ts
        }
    }
`;

export const gqlReplyComment = async comment => {
    const client = await getClient("comments");
    const { data } = await client.mutate({
        mutation: replyToCommentMutation,
        variables: { payload: comment },
    });
    return data;
};

const updateCommentMutation = gql`
    mutation UpdateComment($payload: CommentQuery!) {
        UpdateComment(comment: $payload) {
            archived
            author
            canArchive
            canEdit
            content
            date
            id
            replies
            ts
        }
    }
`;

export const gqlUpdateComment = async (commentId, content) => {
    const client = await getClient("comments");
    const { data } = await client.mutate({
        mutation: updateCommentMutation,
        variables: { payload: { commentId, content } },
    });
    return data;
};

export const gqlArchiveComment = async commentId => {
    const client = await getClient("comments");
    const { data } = await client.mutate({
        mutation: updateCommentMutation,
        variables: { payload: { commentId, archived: true } },
    });
    return { commentId, archived: data.archived };
};

const loadCommentQuery = gql`
    query LoadComment($payload: CommentIdQuery!) {
        LoadComment(commentId: $payload) {
            archived
            author
            canArchive
            canEdit
            content
            date
            id
            replies
            ts
        }
    }
`;

export const gqlLoadComment = async commentId => {
    const client = await getClient("comments");
    const { data } = await client.query({
        query: loadCommentQuery,
        variables: { payload: commentId },
    });
    return data;
};

const loadAllCommentsQuery = gql`
    query LoadAllComments {
        LoadAllComments {
            archived
            author
            canArchive
            canEdit
            content
            date
            id
            replies
            ts
        }
    }
`;

export const gqlLoadAllComments = async () => {
    const client = await getClient("comments");
    const { data } = await client.query({
        query: loadAllCommentsQuery,
    });
    return data;
};

const CONSTANT_COMMENT_UPDATE = false;
let HAS_RUN_SUBSCRIBE = 10;

const mocks = {
    archiveComment: gqlArchiveComment,
    createComment: gqlCreateComment,
    deleteComment: gqlDeleteComment,
    loadComment: gqlLoadComment,
    replyComment: gqlReplyComment,
    // MSW has no GQL subscribe functionality at the moment, so this just returns a list of comments that auto updates if desired for now
    // See https://github.com/mswjs/msw/issues/285 for more info regarding MSW and subscriptions
    subscribeComments: (subject, emit) => {
        const subscribeFn = subject.startsWith("id")
            ? () => gqlLoadComment(subject)
            : gqlLoadAllComments;
        // todo: if subscribe to chunkId, return 2-5 comments of different ids, updating every now and then
        // todo: if subscribe to commentId, update just that comment frequently and always return just that one!
        if (CONSTANT_COMMENT_UPDATE) {
            const interval = window.setInterval(async () => {
                emit(await subscribeFn());
            }, ONE_SECOND * 10);
            return () => window.clearInterval(interval);
        }
        setTimeout(async () => {
            emit(await subscribeFn());
        }, ONE_SECOND * HAS_RUN_SUBSCRIBE);
        HAS_RUN_SUBSCRIBE = ONE_SECOND * 10;
        return () => null;
    },
    updateComment: gqlUpdateComment,
};

// v2v3Bridge.comments.<fnname> may not be available when the v3 js file is loaded, so we need to run the condition on each execution
const makeV2BridgeOrMock = (v2Fn, mockFn) => (...args) => window?.v2v3Bridge?.comments?.[v2Fn](...args) || mockFn(...args);

export const createComment = makeV2BridgeOrMock("newComment", mocks.createComment);
export const replyToComment = makeV2BridgeOrMock("replyComment", mocks.replyComment);
export const loadComment = makeV2BridgeOrMock("fetchComment", mocks.loadComment);
export const updateComment = makeV2BridgeOrMock("editComment", mocks.updateComment);
export const archiveComment = makeV2BridgeOrMock("archiveComment", mocks.archiveComment);
export const deleteComment = makeV2BridgeOrMock("archiveComment", mocks.deleteComment);
export const subscribeComments = makeV2BridgeOrMock("subscribeComments", mocks.subscribeComments);
